import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { DmThread } from "./dmThreads";

/**
 * 進行中取引（全プロジェクト横断）。
 *
 * `DmThread` を継承し、横断一覧の表示で必要な所属プロジェクト情報
 * （`project_id` / `project_name`）を追加で持つ。
 * BE: `GET /api/me/transactions`（`DmTransactionSerializer`）。
 */
export interface Transaction extends DmThread {
  project_id: string | null;
  project_name: string | null;
  /**
   * 振込申請リストでの収支方向。
   * `incoming` = 受託した対価（プラス）、`outgoing` = 自分が発注し
   * `balance_settled` から控除された支出（マイナス）。
   * 進行中取引一覧（`getTransactions`）では未設定。
   */
  direction?: "incoming" | "outgoing";
  /**
   * 表示用の符号付き金額（税抜・整数円）。`incoming` は対価、`outgoing` は
   * settled から実際に控除された額のマイナス値。表示時に税込変換する。
   */
  display_amount?: number | null;
  /**
   * 所属プロジェクトが新 SPA 決済モデル（4 バケット残高 / PaymentLedger）か。
   * `false` のときは旧環境取引で、新 SPA の取引対価（`balance_settled`）と
   * 紐づかない。進行中モーダルの上部サマリと内訳が一致しない原因になるため、
   * 一覧では「旧環境」バッジで判別する。
   * BE 側は `DmTransactionSerializer.get_is_new_payment_model` が
   * `Product.is_new_payment_model` をそのまま公開する。
   */
  is_new_payment_model?: boolean;
}

export interface TransactionsData {
  count: number;
  transactions: Transaction[];
}

export interface TransactionsResponse {
  status: string;
  data: TransactionsData;
}

/**
 * 振込申請対象（受託者として検収完了・未申請の新環境オファー）一覧。
 * `settled_balance` は実際の申請原資（税抜・整数円文字列）で、申請額と一致する。
 */
export interface WithdrawableTransactionsData extends TransactionsData {
  settled_balance: string;
}

export interface WithdrawableTransactionsResponse {
  status: string;
  data: WithdrawableTransactionsData;
}

export const transactionsApi = createApi({
  reducerPath: "transactionsApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Transactions"],
  endpoints: (builder) => ({
    /**
     * ログインユーザーが委託者 / 受託者として関わる進行中
     * （ドラフトを除く未完了 = contractor_pending / in_progress /
     * inspection_pending）の取引を全プロジェクト横断で取得する。
     * GlobalHeader のアカウントメニュー「進行中」（対価合計）と一覧モーダルが利用する。
     */
    getTransactions: builder.query<TransactionsResponse, void>({
      query: () => `/api/me/transactions`,
      providesTags: ["Transactions"],
      keepUnusedDataFor: 30,
    }),

    /**
     * 振込申請の対象となるオファー（受託者として検収完了・未申請かつ
     * `is_new_payment_model=True`）を全プロジェクト横断で取得する。
     * グローバルメニュー「振込申請」の金額表示と確認リストが利用する。
     */
    getWithdrawableTransactions: builder.query<
      WithdrawableTransactionsResponse,
      void
    >({
      query: () => `/api/me/withdrawable-transactions`,
      providesTags: ["Transactions"],
      keepUnusedDataFor: 30,
    }),
  }),
});

export const {
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useGetWithdrawableTransactionsQuery,
} = transactionsApi;
